export const WHY_I_CREATED_ARTICLE_BLOCKS = [
  {
    type: 'p',
    segments: [
      {
        text:
          'Seven years. Six losses. Two children, both born through the same surrogate. And one thing I learned over and over: getting pregnant was easy. Staying pregnant was the seven-year story.',
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          "I don't think I understood that when I started. In 2018, I was thirty-seven, and I'd just begun trying. The first positive test felt like the hard part was over. My fertility battle really had just begun.",
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          'The first loss came early. So did the second. After each one, the same conversation: my doctor said it was probably egg quality, that I was over thirty-five, that we should try again. There\'s a clinical logic to that. Most patients with recurrent pregnancy loss (3 or more) don\'t have a knowable cause, and most women who lose one go on to carry the next. But that logic depends on the next one working. When it doesn\'t, and then doesn\'t again, you start to feel like you\'re in a story the medical system doesn\'t know how to read. I was unlucky. My losses were in less than 1% of women.',
      },
    ],
  },
  {
    type: 'pullQuoteWrap',
    quoteParagraph:
      '\u201cThe grief is also physical. You don\u2019t just lose a pregnancy. You lose it through a procedure or on a bathroom floor or driving in a car.\u201d',
    bodyParagraphs: [
      {
        segments: [
          {
            text:
              'The first two D&Cs were unmedicated. They were scheduled over lunch, when the office was empty. My husband sat in the waiting room and could hear me screaming through the office walls. That is the part of recurrent loss almost no one writes about. The grief is also physical. You don\'t just lose a pregnancy. You lose it through a procedure or on a bathroom floor or driving in a car.',
          },
        ],
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          'After my third loss, doctors finally categorize you as recurrent pregnancy loss. Bloodwork, genetic panels, the standard recurrent-pregnancy-loss evaluation happens next. Everything came back normal. Unexplained. That word is supposed to be a comfort. ',
      },
      { text: "We didn't find anything wrong with you.", italic: true },
      { text: " But it's the opposite. It means there's nothing to fix." },
    ],
  },
  {
    type: 'p',
    segments: [
      { text: 'One fertility doctor said the only thing that ever felt true: ' },
      { text: '"You might have something they haven\'t discovered yet."', italic: true },
      {
        text:
          " My grandmother had six losses. So have I. But the answer will never be known. It's taken me years to accept that. Even today, I was recently diagnosed with an autoimmune disease and the first thing I googled is if Rheumatoid Arthritis caused recurrent pregnancy loss. It doesn't.",
      },
    ],
  },
  { type: 'hr' },
  {
    type: 'p',
    segments: [
      {
        text:
          "I want to tell you about the 2 a.m. of those years, because it's the part I keep coming back to. It wasn't a metaphor. It was literal. I was awake at 2 a.m. a lot, in those years. Pregnant, terrified, Googling every symptom, certain that any minute the bleeding would start. Every time I went to the bathroom, I was afraid of what I'd see. I refreshed forums. I searched for things I would never have asked a doctor in daylight. I looked for women who had lost as many as I had and were still standing.",
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          "Recurrent pregnancy loss at the level I was experiencing, six losses, no diagnosis, no obvious cause, is rare enough that almost no one writes about it. The fertility content on the internet is for the woman who gets pregnant once and has a baby once, or for the woman who can't get pregnant at all. I was neither. I could get pregnant. I just couldn't keep them. And there was no app, no clinician, no friend group that could hold the actual shape of what I was going through.",
      },
    ],
  },
  {
    type: 'pullQuoteWrap',
    quoteAlign: 'left',
    quoteParagraph:
      '\u201cEven when I changed my status in Flo, in What to Expect, pregnant, it never lasted. The next loss came, and the apps would forget me.\u201d',
    bodyParagraphs: [
      {
        segments: [
          {
            text:
              'I was angry, often. Angry at my body. Angry that the answer was always to try again. Angry that the system kept treating each loss as a discrete event, when I was living it as one continuous story. Even when I changed my status in Flo, in What to Expect, ',
          },
          { text: 'pregnant', italic: true },
          {
            text:
              ", it never lasted. The next loss came, and the apps would forget me. They'd send a single email, ",
          },
          { text: 'Sorry for your loss', italic: true },
          {
            text:
              ", and reset me to the start. Different cycle, different week, different intake screens. As if the woman they'd been talking to last month had been someone else.",
          },
        ],
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          "By the time I started IVF, I had already lost three pregnancies. The doctors hoped that genetic screening would tell us something the workup hadn't.",
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          "The first cycle gave us a healthy embryo. PGT-tested. Chromosomally normal. Everything you're supposed to want. When I got pregnant, I thought finally this is going to work.",
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          'Eight weeks later, a heartbeat was found, but soon after we lost the pregnancy.',
      },
    ],
  },
  {
    type: 'p',
    segments: [
      { text: 'That loss was different from the others. The first three I could file under ' },
      { text: 'unexplained', italic: true },
      {
        text:
          ". This one wasn't unexplained. We did the science correctly and it still didn't work. That's a harder grief to carry, because it dissolves the last belief you had about what's possible to know.",
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          'What followed was a stretch I sometimes have trouble narrating in order. Two more natural conceptions. Both lost. Somewhere in there, an ectopic pregnancy, which the medical world treats with methotrexate, a chemotherapy drug given to dissolve the tissue. I recall my doctor saying, this will be painful. By the end of the seven years I had been through four D&Cs total, two of them unmedicated, two of them medicated, and one round of methotrexate. The grief was constant. So was the physical recovery.',
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          'I was approaching the age where my window to use my own eggs was beginning to close. I decided to do IVF as many times as it took. We did four cycles back to back. The first gave us one healthy embryo. The second was cancelled. Cysts on my ovaries. The third produced no healthy embryos at all, which is its own kind of grief. You go through everything, the shots, the monitoring, the retrieval, and the result is nothing. The fourth gave us one healthy embryo. After a total of five rounds of IVF, I knew I was done.',
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          'But that meant we had two embryos. Two shots. One chance per child.',
      },
    ],
  },
  { type: 'hr' },
  {
    type: 'p',
    segments: [
      {
        text:
          "My fertility doctor was the one who recommended surrogacy. He didn't soften it. He said: ",
      },
      {
        text:
          'given your history, given your age, given that we have two healthy embryos, the best odds for you to have a baby is to not be the one carrying.',
        italic: true,
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          'We found her through a surrogate agency, the woman who would carry both of my children, and the word ',
      },
      { text: 'selfless', italic: true },
      {
        text:
          ' took on a new meaning for me. She gave birth to my daughter November 30, 2023. She then gave birth to my son on November 5, 2025. She is still part of my family\'s story. I am still paying for the breast milk she pumps for him. With my daughter, she pumped for an entire year and she was never on formula. I often wonder why I was given such a beautiful gift to have her in our lives.',
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          "Now I'm deep in sleep and potty training, two under two, and I cannot imagine my life any other way. It was worth every ounce of anger, pain, discomfort, and grief. They have changed who I am.",
      },
    ],
  },
  { type: 'hr' },
  {
    type: 'p',
    segments: [
      {
        text:
          "I should tell you why I'm writing this on the front page of a company. It's been such a private journey until this blog.",
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          "Somewhere in the middle of those years, I started noticing the shape of what was missing. It wasn't a lack of information. There's plenty of information. It was that nothing held my whole story. Every clinic visit started over. Every app reset to a new cycle or after a loss. Every well-meaning friend asked me where I was on my fertility journey, and I had to decide how much to explain. The 2 a.m. searches led me to forum threads from years before, useful, but stripped of the woman who wrote them. The medical system filed each loss as an event. I was the only one keeping the through-line.",
      },
    ],
  },
  {
    type: 'p',
    segments: [{ text: "The whole point of OrivonAI is that you shouldn't have to be." }],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          'OrivonAI is a health platform that adapts as your journey changes. It remembers your history. It knows your stage. It surfaces the support you need, when you need it, across fertility, pregnancy, postpartum, and motherhood, and into the chapters between them. Loss, recurrence, surrogacy, and all the months in between. Nova is the conversational layer on top, the way you talk to the platform, but the intelligence is in the platform itself, holding the through-line that I had to hold alone.',
      },
    ],
  },
  {
    type: 'p',
    segments: [
      {
        text:
          "I wanted, for seven years, a place that already knew my story. I didn't want to repeat myself. A place that would connect me to women living the same chapter, in the same week. A place that would surface the support group I didn't know existed, the event I would have missed, the article that would have answered the question I was about to Google at 2 a.m. A place that would carry what was coming next, whether that was a stim shot reminder, a beta test result, or potty training.",
      },
    ],
  },
  {
    type: 'p',
    segments: [{ text: "That place didn't exist. So I'm building it." }],
  },
];
